/**
 * Status derivation for the new-UX Timeline Items table + details panel, rendered through
 * StatusChip (newux/kit/controls.tsx) per the "Status chips" reference sheet.
 */
import {
  AutomationActionStatus,
  AutomationStatus,
  ConcreteExperienceMessage,
  ITaskDenormalized,
  MessageStatus,
  SubTaskStatus,
  SubTaskType,
  TaskStatus
} from '@dadohr/shared/domainTypes/experience';
import moment from 'moment-timezone';
import { UnsafeTFunction } from '../../../../../i18next';
import {
  StatusChipTone,
  StatusChipVariant
} from '../../../components/Builders/newux/kit/controls';
import { SECOND_MESSAGE_STATUS_CUT_OFF } from './MessageDoneSendStatus';
import {
  fromMoment,
  Timestamp,
  toMoment,
  tsIsAfter
} from '../../../../../services/time';
import { ItemData } from '../../../../../services/participatDialog/participantDialogHelpers';
import { joinWithCommaAndAnd } from '../../../../../services/enumerate';
import { useRoutes } from '../../../../../routes';
import { getItemDisplayName } from '../../../helpers/adminName';

export type ChipStyle = { tone: StatusChipTone; variant?: StatusChipVariant };

/**
 * A task's TaskStatus enum has no ERROR value — like the classic Task.tsx, an errored task
 * is derived from its start messages' delivery status instead. Mirrors
 * TaskStatusWithConcreteMessagesCheck (Task.tsx:489-537), including the pre-migration
 * cutoff below which concrete messages aren't reliably present.
 */
export type TaskErrorState = 'ERROR' | 'IN_DELIVERY' | null;

export const getTaskErrorState = (
  task: ITaskDenormalized,
  concreteMessagesByTask: { [taskId: string]: ConcreteExperienceMessage[] }
): TaskErrorState => {
  if (
    task.status === TaskStatus.COMPLETE ||
    task.status === TaskStatus.SKIPPED ||
    task.status === TaskStatus.NOT_STARTED
  ) {
    return null;
  }
  if (task.start.toMillis() < SECOND_MESSAGE_STATUS_CUT_OFF) {
    return null;
  }
  const startMessages = (concreteMessagesByTask[task.id] || []).filter(
    (m) =>
      m.type === 'TASK_START' ||
      m.type === 'TASK_START_PER_COHORT' ||
      m.type === 'EXPERIENCE_MESSAGE'
  );
  if (!startMessages.length) return 'IN_DELIVERY';
  if (startMessages.find((m) => m.status === 'OK')) return null;
  // Closes new-main-1cwf: a BOUNCED/ERROR message must surface as Error even when
  // another message is still QUEUED.
  if (startMessages.find((m) => m.status === 'BOUNCED' || m.status === 'ERROR')) {
    return 'ERROR';
  }
  if (startMessages.find((m) => m.status === 'QUEUED')) return 'IN_DELIVERY';
  return 'ERROR';
};

/**
 * The specific concrete message an errored Task/Message item's status is derived from —
 * same selection as getTaskErrorState's Task branch, and MessageDoneSendStatus's Message
 * branch — so it can be fed into the SENT_ITEM issue/legacyCopy system for inline error
 * text. Automations resolve their error text per-action instead (see
 * getActionErrorDetailsText in ParticipantDialog.tsx), not through this.
 */

export const getMessageErrorState = (
  msgId: string,
  status: MessageStatus,
  concreteMessagesByMessage: { [msgId: string]: ConcreteExperienceMessage[] }
): TaskErrorState => {
  if (status !== MessageStatus.DONE) return null;
  const messages = concreteMessagesByMessage[msgId] || [];
  if (!messages.length) return null;
  if (messages.find((m) => m.status === 'BOUNCED' || m.status === 'ERROR')) {
    return 'ERROR';
  }
  return null;
};

export const getItemErrorConcreteMessage = (
  item: ItemData,
  concreteMessagesByTask: { [taskId: string]: ConcreteExperienceMessage[] },
  concreteMessagesByMessage: { [msgId: string]: ConcreteExperienceMessage[] }
): ConcreteExperienceMessage | null => {
  if (item.type === 'TASK') {
    const startMessages = (concreteMessagesByTask[item.data.id] || []).filter(
      (m) =>
        m.type === 'TASK_START' ||
        m.type === 'TASK_START_PER_COHORT' ||
        m.type === 'EXPERIENCE_MESSAGE'
    );
    return startMessages.find((m) => m.status === 'BOUNCED' || m.status === 'ERROR') || null;
  }
  if (item.type === 'MESSAGE') {
    const messages = concreteMessagesByMessage[item.data.templateId] || [];
    return messages.find((m) => m.status === 'BOUNCED' || m.status === 'ERROR') || null;
  }
  return null;
};

/**
 * Sort key / single-date fallback for the table's date column. Tasks are date RANGES
 * (see getItemDateRange) — this returns the task's start for sort-ordering purposes,
 * not what's actually displayed in the task row.
 */
export const getItemDate = (item: ItemData): Timestamp | null => {
  if (item.type === 'TASK') {
    return item.data.start;
  }
  if (item.type === 'MESSAGE') {
    return item.data.sendAt;
  }
  // AUTOMATION
  if (item.data.completedAt) return item.data.completedAt;
  if (item.data.timing.type === 'ABSOLUTE') return item.data.timing.start;
  return null;
};

/**
 * The originally-scheduled date, for comparison against getItemActualDate below.
 * Unlike getItemDate, this never substitutes completedAt for an automation — an
 * automation's scheduled trigger time and its completion time are different things,
 * and comparing completedAt against itself would always read as "on time".
 */
export const getItemScheduledDate = (item: ItemData): Timestamp | null => {
  if (item.type === 'TASK') return item.data.start;
  if (item.type === 'MESSAGE') return item.data.sendAt;
  // AUTOMATION
  return item.data.timing.type === 'ABSOLUTE' ? item.data.timing.start : null;
};

/**
 * Tasks display as a start–deadline range in the table (e.g. "24 Jun–28 Jun")
 * Messages/Automations are single-point-in-time and have no range
 */
export const getItemDateRange = (
  item: ItemData
): { start: Timestamp; end: Timestamp } | null => {
  if (item.type !== 'TASK') return null;
  return { start: item.data.start, end: item.data.end };
};

/**
 * The actual start/send/trigger time on record for an item, distinct from the
 * scheduled time getItemDate returns. Tasks/Messages derive this from their
 * concrete messages (mirrors old UX's TaskStartDateWithConcreteMessagesCheck in
 * Task.tsx and MessageSendDate.tsx). Automations have no "attempted/errored at"
 * timestamp on the record today, so completedAt is used as a proxy — this only
 * reflects a late-but-eventually-successful run, not a still-erroring one.
 */
export const getItemActualDate = (
  item: ItemData,
  concreteMessagesByTask: { [taskId: string]: ConcreteExperienceMessage[] },
  concreteMessagesByMessage: { [msgId: string]: ConcreteExperienceMessage[] }
): Timestamp | null => {
  if (item.type === 'TASK') {
    const startMessages = (concreteMessagesByTask[item.data.id] || [])
      .filter(
        (m) =>
          m.type === 'TASK_START' ||
          m.type === 'TASK_START_PER_COHORT' ||
          m.type === 'EXPERIENCE_MESSAGE'
      )
      .sort((a, b) => a.at.toMillis() - b.at.toMillis());
    return startMessages[0]?.at || null;
  }
  if (item.type === 'MESSAGE') {
    const messages = (concreteMessagesByMessage[item.data.templateId] || [])
      .slice()
      .sort((a, b) => a.at.toMillis() - b.at.toMillis());
    return messages[messages.length - 1]?.at || null;
  }
  // AUTOMATION
  return item.data.completedAt || null;
};

/**
 * Whether an item's actual date differs from its scheduled date by more than a
 * day — mirrors old UX Task.tsx's same-day comparison for "should have started".
 */
export const isItemLate = (
  scheduled: Timestamp | null,
  actual: Timestamp | null
): boolean => {
  if (!scheduled || !actual) return false;
  return !toMoment(scheduled).isSame(toMoment(actual), 'day');
};

/**
 * "Start today" (Task) / "Send today" (Message) / "Run today" (Automation) eligibility: —
 * Task.tsx's isTaskAfterToday requires the start be at least 2 days out
 * Message.tsx/Automation.tsx's isInFuture allows any day after today
 * Only applies to items that haven't started yet: a fixed-date item can be moved to today
 * (stages locally via setD, needs Save); a trigger-based item has no date to move, so it's
 * force-triggered now instead.
 */
export type StartTodayEligibility = 'FIXED_DATE' | 'TRIGGERED' | null;

export const getStartTodayEligibility = (
  item: ItemData,
  category: StatusCategory
): StartTodayEligibility => {
  if (category !== 'UPCOMING') return null;
  if (item.triggeredBy?.length) return 'TRIGGERED';
  const date = getItemDate(item);
  if (!date) return null;
  if (item.type === 'TASK') {
    return tsIsAfter(date, fromMoment(moment().startOf('day').add(1, 'days')))
      ? 'FIXED_DATE'
      : null;
  }
  return moment.unix(date.seconds).diff(moment(), 'days') > 0 ? 'FIXED_DATE' : null;
};

export type StatusCategory =
  | 'DONE'
  | 'ERROR'
  | 'SKIPPED'
  | 'UPCOMING'
  /** Task-only: RUNNING/LATE/PAST_DEADLINE — "in progress or overdue", not yet done. */
  | 'ACTIONABLE';

export const getItemStatusChipConfig = (
  item: ItemData,
  t: UnsafeTFunction,
  taskErrorState: TaskErrorState = null,
  messageErrorState: TaskErrorState = null
): [string, ChipStyle] => {
  if (item.type === 'TASK') {
    if (taskErrorState === 'ERROR') return [t('Error'), { tone: 'red', variant: 'dark' }];
    if (taskErrorState === 'IN_DELIVERY') return [t('In delivery'), { tone: 'grey', variant: 'light' }];
    switch (item.data.status) {
      case TaskStatus.NOT_STARTED:
        return [t('Upcoming'), { tone: 'grey', variant: 'light' }];
      case TaskStatus.RUNNING:
        return [t('To Do'), { tone: 'blue', variant: 'light' }];
      case TaskStatus.LATE:
        return [t('Overdue'), { tone: 'red', variant: 'light' }];
      case TaskStatus.COMPLETE:
        return [t('Done'), { tone: 'green', variant: 'light' }];
      case TaskStatus.SKIPPED:
        return [t('Skipped'), { tone: 'grey', variant: 'dark' }];
      case TaskStatus.PAST_DEADLINE:
        return [t('Past Deadline'), { tone: 'grey', variant: 'light' }];
      default:
        return [t('Upcoming'), { tone: 'grey', variant: 'light' }];
    }
  }
  if (item.type === 'MESSAGE') {
    if (messageErrorState === 'ERROR') return [t('Error'), { tone: 'red', variant: 'dark' }];
    switch (item.data.status) {
      case MessageStatus.UPCOMING:
        return [t('Upcoming'), { tone: 'grey', variant: 'light' }];
      case MessageStatus.TRIGGERED:
        return [t('Triggered'), { tone: 'grey', variant: 'light' }];
      case MessageStatus.DONE:
        return [t('Sent'), { tone: 'green', variant: 'light' }];
      case MessageStatus.ERROR:
        return [t('Error'), { tone: 'red', variant: 'dark' }];
      case MessageStatus.SKIPPED:
        return [t('Skipped'), { tone: 'grey', variant: 'dark' }];
      default:
        return [t('Upcoming'), { tone: 'grey', variant: 'light' }];
    }
  }
  // AUTOMATION
  switch (item.data.status) {
    case AutomationStatus.UPCOMING:
      return [t('Upcoming'), { tone: 'grey', variant: 'light' }];
    case AutomationStatus.TRIGGERED:
      return [t('Triggered'), { tone: 'grey', variant: 'light' }];
    case AutomationStatus.DONE:
      return [t('Done'), { tone: 'green', variant: 'light' }];
    case AutomationStatus.ERROR:
      return [t('Error'), { tone: 'red', variant: 'dark' }];
    case AutomationStatus.SKIPPED:
      return [t('Skipped'), { tone: 'grey', variant: 'dark' }];
    default:
      return [t('Upcoming'), { tone: 'grey', variant: 'light' }];
  }
};

export const getItemStatusCategory = (
  item: ItemData,
  taskErrorState: TaskErrorState = null,
  messageErrorState: TaskErrorState = null
): StatusCategory => {
  if (item.type === 'TASK') {
    if (taskErrorState === 'ERROR') return 'ERROR';
    switch (item.data.status) {
      case TaskStatus.COMPLETE:
        return 'DONE';
      case TaskStatus.SKIPPED:
        return 'SKIPPED';
      case TaskStatus.NOT_STARTED:
        return 'UPCOMING';
      case TaskStatus.RUNNING:
      case TaskStatus.LATE:
      case TaskStatus.PAST_DEADLINE:
        return 'ACTIONABLE';
      default:
        return 'UPCOMING';
    }
  }
  if (item.type === 'MESSAGE') {
    if (messageErrorState === 'ERROR') return 'ERROR';
    switch (item.data.status) {
      case MessageStatus.DONE:
        return 'DONE';
      case MessageStatus.SKIPPED:
        return 'SKIPPED';
      case MessageStatus.ERROR:
        return 'ERROR';
      default:
        return 'UPCOMING';
    }
  }
  // AUTOMATION
  switch (item.data.status) {
    case AutomationStatus.DONE:
      return 'DONE';
    case AutomationStatus.SKIPPED:
      return 'SKIPPED';
    case AutomationStatus.ERROR:
      return 'ERROR';
    default:
      return 'UPCOMING';
  }
};

/** Task/Message resend eligibility: any status except UPCOMING or SKIPPED. Automations are never resendable. */
export const canResendItem = (item: ItemData, taskErrorState: TaskErrorState = null): boolean => {
  if (item.type === 'AUTOMATION') return false;
  const cat = getItemStatusCategory(item, taskErrorState);
  return cat !== 'UPCOMING' && cat !== 'SKIPPED';
};

/** True for a task with a DocuSign envelope subtask — resending it won't resend the Docusign notification itself. */
export const hasDocusignSubtask = (item: ItemData): boolean =>
  item.type === 'TASK' &&
  item.data.subTasks.some((s) => s.type === SubTaskType.DOCUSIGN_ENVELOPE_SIGN);

/**
 * "When task completes" items are categorized as UPCOMING, but can't actually be skipped
 * (yet – feature might be added in future) but the button shouldn't be shown because it 
 * doesn't currently work.
 * Not needed for "When conditions met" or "When data changes" since they don't appear in 
 * the list until triggered.
 */
export const isSkippableCategory = (item: ItemData, category: StatusCategory): boolean =>
  !item.triggeredBy?.length &&
  (category === 'UPCOMING' || category === 'ERROR' || category === 'ACTIONABLE');

/** The subset of a multi-select that "Skip all" should actually act on — same rule as isSkippableCategory. */
export const getSkippableItems = (items: ItemData[], categories: StatusCategory[]): ItemData[] =>
  items.filter((i, idx) => isSkippableCategory(i, categories[idx]));

export const canSkipItem = (
  item: ItemData,
  taskErrorState: TaskErrorState = null,
  messageErrorState: TaskErrorState = null
): boolean =>
  isSkippableCategory(item, getItemStatusCategory(item, taskErrorState, messageErrorState));

export const canUnskipItem = (item: ItemData): boolean =>
  getItemStatusCategory(item) === 'SKIPPED';

export const canUncompleteItem = (item: ItemData): boolean =>
  getItemStatusCategory(item) === 'DONE';

export const isErrorItem = (
  item: ItemData,
  taskErrorState: TaskErrorState = null,
  messageErrorState: TaskErrorState = null
): boolean =>
  getItemStatusCategory(item, taskErrorState, messageErrorState) === 'ERROR';

export type MultiSelectActionEligibility = {
  anyDone: boolean;
  anyError: boolean;
  anySkipped: boolean;
  anySkippable: boolean;
  resendableItems: Extract<ItemData, { type: 'TASK' | 'MESSAGE' }>[];
  anyUpcomingResendable: boolean;
};

/**
 * Which bulk actions the multi-select details panel should offer for a set of selected
 * items, given each item's already-derived StatusCategory (same order as `items`).
 */
export const getMultiSelectActionEligibility = (
  items: ItemData[],
  categories: StatusCategory[]
): MultiSelectActionEligibility => {
  const anyDone = items.some((i, idx) => i.type === 'TASK' && categories[idx] === 'DONE');
  // Tasks have no Retry action (classic offers only Resend for an errored task) — only
  // Message/Automation errors make "Retry all errors" meaningful.
  const anyError = items.some((i, idx) => i.type !== 'TASK' && categories[idx] === 'ERROR');
  const anySkipped = categories.includes('SKIPPED');
  const anySkippable = items.some((i, idx) => isSkippableCategory(i, categories[idx]));
  const resendableItems = items.filter(
    (i, idx) =>
      i.type !== 'AUTOMATION' && categories[idx] !== 'UPCOMING' && categories[idx] !== 'SKIPPED'
  ) as Extract<ItemData, { type: 'TASK' | 'MESSAGE' }>[];
  const anyUpcomingResendable = items.some(
    (i, idx) => i.type !== 'AUTOMATION' && categories[idx] === 'UPCOMING'
  );

  return { anyDone, anyError, anySkipped, anySkippable, resendableItems, anyUpcomingResendable };
};

/**
 * Subtask/automation-action "Pending" status is no longer a flat label — it mirrors the
 * parent task's own status (Upcoming vs To do), or is Error if the parent task itself is
 * errored (an errored task's not-yet-done subtasks are errored too — none of them will
 * actually run until the parent's delivery issue is resolved).
 */
export const getSubTaskDisplayStatus = (
  subTaskStatus: SubTaskStatus,
  parentTaskCategory: StatusCategory,
  t: UnsafeTFunction
): [string, ChipStyle] => {
  if (subTaskStatus === SubTaskStatus.DONE) return [t('Done'), { tone: 'green', variant: 'light' }];
  if (subTaskStatus === SubTaskStatus.SKIPPED) return [t('Skipped'), { tone: 'grey', variant: 'dark' }];
  // OPEN
  if (parentTaskCategory === 'ERROR') return [t('Error'), { tone: 'red', variant: 'dark' }];
  if (parentTaskCategory === 'ACTIONABLE') return [t('To do'), { tone: 'blue', variant: 'light' }];
  return [t('Upcoming'), { tone: 'grey', variant: 'light' }];
};

export const getAutomationActionDisplayStatus = (
  status: AutomationActionStatus | 'RUNNING',
  t: UnsafeTFunction
): [string, ChipStyle] => {
  if (status === AutomationActionStatus.DONE) return [t('Done'), { tone: 'green', variant: 'light' }];
  if (status === AutomationActionStatus.SKIPPED) return [t('Skipped'), { tone: 'grey', variant: 'dark' }];
  if (status === AutomationActionStatus.ERROR) return [t('Error'), { tone: 'red', variant: 'dark' }];
  if (status === 'RUNNING') return [t('Running'), { tone: 'blue', variant: 'light' }];
  return [t('Upcoming'), { tone: 'grey', variant: 'light' }];
};

/**
 * Trigger-chain description for an item whose start is derived from another task
 * completing (item.triggeredBy — populated by _mapFn/participatDialogHelpers.ts for the
 * 'triggered' template timing type). Returns null for items with a fixed schedule.
 *
 * Data-change-triggered items ("Starts when Manager changes", "...conditions
 * met") are NOT covered here — those items have no instance record at all until their
 * watched field/rule actually matches (confirmed against the backend's
 * mutableAddDataChangeItems/evaluateDataChangeForUser), so they never reach ItemData in
 * the first place. 
 */
export const getTriggerDescription = (
  item: ItemData,
  t: UnsafeTFunction
): { short: string; verb: string; parentNames: string; full: string } | null => {
  if (!item.triggeredBy?.length) return null;

  const short =
    item.type === 'MESSAGE'
      ? t('Sends when…')
      : item.type === 'AUTOMATION'
      ? t('Runs when…')
      : t('Starts when…');

  const parentNames = joinWithCommaAndAnd(
    item.triggeredBy.map((p) => getItemDisplayName(p)),
    t
  );

  // `verb` is the bolded lead-in (e.g. "Sends when task completes") — kept separate from
  // `parentNames` so callers can style them differently (see TimelineItemDetailsPanel's
  // TriggerLine). i18next treats ':' inside a t() key as a namespace separator, so the
  // colon in `full` is appended outside the translated text.
  const verb =
    item.type === 'MESSAGE'
      ? t('Sends when task completes')
      : item.type === 'AUTOMATION'
      ? t('Runs when task completes')
      : t('Starts when task completes');
  const full = `${verb}: ${parentNames}`;

  return { short, verb, parentNames, full };
};

export const getVisibleAudienceName = (item: ItemData): string | null => {
  if (!item.audience || item.audience.rule === 'EVERYONE' || !item.audience.name) return null;
  return item.audience.name;
};

export const getTriggerTimelineItemUrl = (
  ROUTES: ReturnType<typeof useRoutes>['ROUTES'],
  tplId: string,
  item: ItemData
): string | null => {
  const parentTaskId = item.triggeredBy?.[0]?.templateId;
  if (!parentTaskId) return null;

  return ROUTES.experiences.details.general.url(tplId, {
    type: 'task',
    taskId: parentTaskId
  });
};

export const getTemplateItemEditUrl = (
  ROUTES: ReturnType<typeof useRoutes>['ROUTES'],
  tplId: string,
  item: ItemData
): string => {
  if (item.type === 'TASK') {
    return ROUTES.experiences.details.edit.url(tplId, {
      step: 'tasks',
      taskId: item.data.templateId
    });
  }
  if (item.type === 'MESSAGE') {
    return ROUTES.experiences.details.edit.url(tplId, {
      step: 'tasks',
      messageId: item.data.templateId
    });
  }
  return ROUTES.experiences.details.edit.url(tplId, {
    step: 'tasks',
    automationId: item.data.templateId
  });
};
